/**
 * AttachmentChipsEditor — ad-hoc file attachments + YouTube link pills for
 * email compose surfaces. Controlled component: parent owns state.
 *
 * Shape:
 *   attachments: [{ id, name, size, content_type, data_b64 }]   — base64-encoded in-memory
 *   youtube:     [string]                                        — canonical URLs
 *
 * Validation (mirrors backend `email_attachments.py`):
 *   - Per-file cap: 3 MB raw
 *   - Aggregate cap: 20 MB raw
 *   - MIME allow-list: pdf/docx/xlsx/pptx/pngjpg/gif/webp/mp4/mov/csv/txt
 *   - YouTube: canonicalised client-side; invalid → toast
 *
 * Used by ComposeModal (Communications email tab) and SendQuoteModal (Lead
 * Focus quote email). Zero external deps — fully inline-styled to match
 * surrounding CRM aesthetic.
 */
import { useRef, useState } from "react";
import { Paperclip, Youtube, X, Plus } from "lucide-react";
import { toast } from "sonner";

const MAX_PER_FILE = 3 * 1024 * 1024;         // 3 MB
const MAX_TOTAL = 20 * 1024 * 1024;           // 20 MB
const ALLOWED_MIME = new Set([
  "application/pdf", "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "image/png", "image/jpeg", "image/gif", "image/webp",
  "video/mp4", "video/quicktime",
  "text/csv", "text/plain",
]);

const YT_RE = /^(https?:\/\/)?(www\.|m\.|music\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i;

function canonicalYouTube(url) {
  const m = (url || "").trim().match(YT_RE);
  if (!m) return null;
  return `https://www.youtube.com/watch?v=${m[5]}`;
}

function fmtBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const result = fr.result || "";
      // result is a data URL: "data:<mime>;base64,<payload>"
      const b64 = String(result).split(",")[1] || "";
      resolve(b64);
    };
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

export default function AttachmentChipsEditor({
  attachments = [],
  youtube = [],
  onAttachmentsChange,
  onYouTubeChange,
  compact = false,
}) {
  const fileInputRef = useRef(null);
  const [ytInput, setYtInput] = useState("");

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const next = [...attachments];
    let total = next.reduce((s, a) => s + (a.size || 0), 0);
    for (const f of files) {
      if (!ALLOWED_MIME.has(f.type)) {
        toast.error(`"${f.name}" — file type not allowed`);
        continue;
      }
      if (f.size > MAX_PER_FILE) {
        toast.error(`"${f.name}" is ${fmtBytes(f.size)} — max ${fmtBytes(MAX_PER_FILE)}`);
        continue;
      }
      if (total + f.size > MAX_TOTAL) {
        toast.error(`Total attachment size would exceed ${fmtBytes(MAX_TOTAL)}`);
        continue;
      }
      try {
        const data_b64 = await readFileAsBase64(f);
        next.push({
          id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          name: f.name,
          size: f.size,
          content_type: f.type,
          data_b64,
        });
        total += f.size;
      } catch {
        toast.error(`Failed to read "${f.name}"`);
      }
    }
    onAttachmentsChange?.(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (id) => {
    onAttachmentsChange?.((attachments || []).filter(a => a.id !== id));
  };

  const addYoutube = () => {
    const canon = canonicalYouTube(ytInput);
    if (!canon) {
      toast.error("Enter a valid YouTube URL (e.g. youtube.com/watch?v=…)");
      return;
    }
    if ((youtube || []).includes(canon)) {
      toast.info("Link already added");
      setYtInput("");
      return;
    }
    onYouTubeChange?.([...(youtube || []), canon]);
    setYtInput("");
  };

  const removeYoutube = (url) => {
    onYouTubeChange?.((youtube || []).filter(u => u !== url));
  };

  const totalSize = (attachments || []).reduce((s, a) => s + (a.size || 0), 0);

  return (
    <div data-testid="attachment-chips-editor" style={{ marginTop: compact ? 6 : 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          data-testid="attach-file-btn"
          onClick={() => fileInputRef.current?.click()}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", border: "0.5px solid var(--border, #ddd)", borderRadius: 6, background: "var(--surface, #fff)", color: "var(--text-secondary, #555)", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
          title="Attach a file (PDF, DOC, image, video…). Max 3 MB each, 20 MB total."
        >
          <Paperclip style={{ width: 11, height: 11 }} /> Attach file
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          data-testid="attach-file-input"
          onChange={e => handleFiles(Array.from(e.target.files || []))}
          style={{ display: "none" }}
        />
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
            <Youtube style={{ width: 12, height: 12, color: "#cc0000", position: "absolute", left: 8 }} />
            <input
              data-testid="attach-youtube-input"
              value={ytInput}
              onChange={e => setYtInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addYoutube(); } }}
              placeholder="Paste YouTube URL…"
              style={{ padding: "4px 8px 4px 24px", border: "0.5px solid var(--border, #ddd)", borderRadius: 6, fontSize: 11, width: 200, fontFamily: "inherit", background: "var(--surface, #fff)" }}
            />
          </div>
          <button
            type="button"
            data-testid="attach-youtube-add"
            onClick={addYoutube}
            style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "4px 8px", border: "0.5px solid var(--border, #ddd)", borderRadius: 6, background: "var(--surface, #fff)", color: "var(--text-secondary, #555)", fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}
            title="Add YouTube link"
          >
            <Plus style={{ width: 10, height: 10 }} /> Add
          </button>
        </div>
        {(attachments.length > 0 || youtube.length > 0) && (
          <span data-testid="attach-summary" style={{ fontSize: 10, color: "var(--text-tertiary, #888)" }}>
            {attachments.length} file{attachments.length === 1 ? "" : "s"} ({fmtBytes(totalSize)})
            {youtube.length > 0 ? ` · ${youtube.length} YouTube` : ""}
          </span>
        )}
      </div>

      {/* Chip list */}
      {(attachments.length > 0 || youtube.length > 0) && (
        <div data-testid="attach-chips" style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
          {attachments.map(a => (
            <div key={a.id} data-testid={`attach-chip-${a.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 5px 3px 8px", background: "#f4f6f8", border: "0.5px solid #e2e5e9", borderRadius: 12, fontSize: 11, color: "#374151", maxWidth: 260 }}>
              <Paperclip style={{ width: 10, height: 10, color: "#6B7280", flexShrink: 0 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }} title={a.name}>{a.name}</span>
              <span style={{ color: "#9CA3AF", fontSize: 10 }}>{fmtBytes(a.size)}</span>
              <button
                type="button"
                onClick={() => removeAttachment(a.id)}
                data-testid={`attach-chip-remove-${a.id}`}
                aria-label={`Remove ${a.name}`}
                style={{ width: 16, height: 16, borderRadius: "50%", border: "none", background: "transparent", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, color: "#9CA3AF" }}
              >
                <X style={{ width: 10, height: 10 }} />
              </button>
            </div>
          ))}
          {youtube.map((url, i) => (
            <div key={url} data-testid={`yt-chip-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 5px 3px 8px", background: "#fff1f1", border: "0.5px solid #f7c1c1", borderRadius: 12, fontSize: 11, color: "#a32d2d", maxWidth: 260 }}>
              <Youtube style={{ width: 11, height: 11, color: "#cc0000", flexShrink: 0 }} />
              <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180 }} title={url}>
                {url.replace(/^https?:\/\/(www\.)?/, "")}
              </a>
              <button
                type="button"
                onClick={() => removeYoutube(url)}
                data-testid={`yt-chip-remove-${i}`}
                aria-label={`Remove ${url}`}
                style={{ width: 16, height: 16, borderRadius: "50%", border: "none", background: "transparent", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, color: "#a32d2d" }}
              >
                <X style={{ width: 10, height: 10 }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Named exports for unit-testing the pure helpers
export { canonicalYouTube, ALLOWED_MIME, MAX_PER_FILE, MAX_TOTAL };
